import React, { CSSProperties, ReactElement, ReactNode, useEffect, useRef } from 'react';
import cn from 'classnames';
import { KeyboardShortcut, Key } from '@repo/components/tooltips';
import GoBack from '../GoBack';
import headerStyles from './Header.module.scss';
import { ChevronLeftIcon } from '@repo/icons';
import { useDispatch, useSelector } from 'react-redux';
import { pinNavBar, selectLastPathname, popOutNavBar, hideNavBar } from '@repo/redux/actions/ui';
import { selectNavBarState } from '@repo/redux/reducers/ui/navBar';
import { PinNavBarButton } from '@layouts/PrimaryLayout/Navigation/NavBarToggle';
import { selectNavBarWidth } from '@repo/redux/reducers/ui/panelSizing';
import { SVGProps } from '@repo/utils/types';

// This could likely be better typed to infer the accepted additional props
// which are spread and passed to any `as` element
interface ActionProps {
  as?: React.ElementType<any>;
  icon?: string; // icon file path, legacy usage
  iconClassName?: string;
  iconActive?: boolean;
  IconComponent?: (iconProps: SVGProps) => ReactElement<any, any>;
  large?: boolean;
  small?: boolean;
  hover?: boolean;
  tooltip?: boolean;
  title?: string;
  shortcut?: string | Array<string>;
  hideTooltip?: boolean;

  asButton?: boolean;
  active?: boolean;
}

export type HeaderActionProps = React.ComponentPropsWithoutRef<any> & ActionProps;

// FIXME we should NOT be using Anchor tags... these should be buttons
function Action({
  as: Component = 'a',
  children,
  icon,
  iconClassName,
  iconActive,
  IconComponent,
  large = false,
  small = false,
  hover = false,
  className,
  tooltip = false,
  title = '',
  shortcut,
  hideTooltip = false,
  asButton = false,
  active,
  ...props
}: HeaderActionProps) {
  className = cn(headerStyles.action, 'text-small', className, {
    [headerStyles.actionSmall]: small,
  });
  let tooltipShortcut = null;

  let content = (
    <Component className={className} {...props}>
      <span
        className={cn(
          large ? headerStyles.actionIconLarge : headerStyles.actionIcon,
          iconClassName,
          active && headerStyles.actionIconActive,
        )}
        style={icon && { backgroundImage: `url(${icon})` }}
      >
        {IconComponent ? <IconComponent active={iconActive} /> : <span>{title}</span>}
      </span>
      {children && <span className={headerStyles.actionContent}>{children}</span>}
    </Component>
  );

  if (asButton) {
    content = (
      <button
        className={cn(headerStyles.actionButton, className)}
        onClick={props.onClick}
        disabled={props.disabled}
      >
        {title}
      </button>
    );
  }

  if (tooltip && !active) {
    if (shortcut) {
      const shortcutParts = typeof shortcut == 'string' ? shortcut.split('') : shortcut;
      tooltipShortcut = (
        <>
          {shortcutParts.map((char, idx) => (
            <Key key={idx}>{char}</Key>
          ))}
        </>
      );
    }

    content = Component == 'button' ? <span>{content}</span> : content;

    return (
      <KeyboardShortcut
        placement="bottom"
        shortcut={
          <>
            {tooltipShortcut}
            {title}
          </>
        }
        hideTooltip={hideTooltip}
      >
        {content}
      </KeyboardShortcut>
    );
  }

  return content;
}

export interface HeaderProps {
  actions?: ReactNode;
  actionsClassName?: string;
  goBack?: boolean;
  after?: ReactNode;
  onGoBack?: () => void;
  title?: ReactNode;
  titleClassName?: string;
  children?: ReactNode;
  style?: CSSProperties;
}

export default function Header({
  children,
  className,
  title,
  titleClassName,
  goBack = false,
  onGoBack,
  actions,
  actionsClassName,
  after,
  style = {},
}: Omit<React.ComponentPropsWithoutRef<'header'>, 'title'> & HeaderProps) {
  // The goal is to keep users in the app if they try to navigate with Clay UI. This is difficult
  // b/c browser security prevents us from looking at a user's history stack. This conditional
  // rendering gets us part of the way there (first route loaded), but we'd likely need a more
  // engineered solution for broader use cases.
  const dispatch = useDispatch();
  const navBarBtnRef = useRef<HTMLButtonElement>(null);
  const canGoBack = useSelector(selectLastPathname);
  const { status, transitioning } = useSelector(selectNavBarState);
  const navBarWidth = useSelector(selectNavBarWidth);
  const showPinNavBarBtn = status !== 'pinned';

  const pinNav = () => dispatch(pinNavBar());
  const popOutNav = () => {
    !transitioning && dispatch(popOutNavBar());
  };

  useEffect(() => {
    const hideNavBarOnClick = () => {
      if (navBarBtnRef.current === null) {
        return;
      }

      dispatch(hideNavBar());
    };

    const hideNavBarOnMouseLeave = (e) => {
      // Width of nav bar is 200px, -8px for right padding
      if ((e?.clientX || 0) < navBarWidth - 8) {
        return;
      }

      dispatch(hideNavBar());
    };

    if (status === 'floating') {
      window.addEventListener('click', hideNavBarOnClick);
      window.addEventListener('mousemove', hideNavBarOnMouseLeave);
      return () => {
        window.removeEventListener('click', hideNavBarOnClick);
        window.removeEventListener('mousemove', hideNavBarOnMouseLeave);
      };
    }
    return () => {};
  }, [status]);

  return (
    <>
      <header className={cn(headerStyles.container, className)} style={style}>
        {showPinNavBarBtn && (
          <PinNavBarButton
            ref={navBarBtnRef}
            className={headerStyles.pinNavBarBtn}
            onClick={transitioning ? undefined : pinNav}
            onMouseEnter={transitioning ? undefined : popOutNav}
          />
        )}
        {goBack && canGoBack && (
          <Header.Action
            className={cn(
              headerStyles.goBack,
              showPinNavBarBtn && headerStyles.goBackWithPinNavBarBtn,
            )}
            as={GoBack}
            IconComponent={ChevronLeftIcon}
            onGoBack={onGoBack}
          />
        )}
        <div className={cn(headerStyles.primary, titleClassName)}>{children || title}</div>
        <div className={cn(headerStyles.actions, actionsClassName)}>{actions ? actions : null}</div>
      </header>
      {after}
    </>
  );
}

Header.Action = Action;
